import React, { useEffect, useState } from "react";
import { X as XIcon, Plus, Trash2, Save, User, Loader2, AlertCircle, Star, Send, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

/**
 * TeamManagersModal — admin UI to view/edit/add/remove the "responsables"
 * (managers) attached to a team. The first entry is the primary responsable
 * (the one who originally registered the team) and is synced back to the
 * team's legacy fields + linked registration by the backend.
 */
export default function TeamManagersModal({ open, teamId, teamName, onClose }) {
    const [rows, setRows] = useState([]);
    const [savedRows, setSavedRows] = useState([]); // last-persisted state, used to know which rows can be SMS'ed
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveOk, setSaveOk] = useState(false);
    const [error, setError] = useState(null);
    const [smsBusy, setSmsBusy] = useState(null); // idx currently sending
    const [smsResult, setSmsResult] = useState({}); // { idx: {ok, sent} }

    useEffect(() => {
        if (!open || !teamId) return;
        setLoading(true);
        setError(null);
        setSmsResult({});
        api.get(`/admin/teams/${teamId}/managers`)
            .then(({ data }) => {
                const list = data.managers || [];
                const rowsList = list.length > 0 ? list : [{ name: "", email: "", phone: "" }];
                setRows(rowsList);
                setSavedRows(list); // only persisted rows can be SMS'ed
            })
            .catch((ex) => setError(ex?.response?.data?.detail || ex.message))
            .finally(() => setLoading(false));
    }, [open, teamId]);

    if (!open) return null;

    const updateRow = (idx, field, value) => {
        setRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
    };

    const addRow = () => setRows((r) => [...r, { name: "", email: "", phone: "" }]);

    const removeRow = (idx) => {
        setRows((r) => (r.length <= 1 ? r : r.filter((_, i) => i !== idx)));
    };

    const makePrimary = (idx) => {
        if (idx === 0) return;
        setRows((r) => {
            const copy = [...r];
            const [item] = copy.splice(idx, 1);
            copy.unshift(item);
            return copy;
        });
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        setSaveOk(false);
        try {
            const payload = { managers: rows.filter((r) => r.name || r.email || r.phone) };
            const { data } = await api.put(`/admin/teams/${teamId}/managers`, payload);
            setRows(data.managers);
            setSavedRows(data.managers);
            setSaveOk(true);
            setTimeout(() => setSaveOk(false), 2500);
        } catch (ex) {
            setError(ex?.response?.data?.detail || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const sendSms = async (idx) => {
        const row = rows[idx];
        if (!row?.phone || !row?.phone.trim()) return;
        setSmsBusy(idx);
        setSmsResult((r) => ({ ...r, [idx]: null }));
        try {
            const { data } = await api.post(`/admin/teams/${teamId}/managers/send_link`, {
                phone: row.phone.trim(),
                name: row.name || "",
            });
            setSmsResult((r) => ({ ...r, [idx]: { ok: !!data.sms_sent, reason: data.sms?.reason || "" } }));
        } catch (ex) {
            setSmsResult((r) => ({ ...r, [idx]: { ok: false, reason: ex?.response?.data?.detail || ex.message } }));
        } finally {
            setSmsBusy(null);
        }
    };

    // A row can be SMS'ed only if it matches a saved row (same phone) — avoids
    // sending to unsaved edits and keeps behaviour predictable.
    const isSavedPhone = (phone) => {
        const p = (phone || "").trim();
        if (!p) return false;
        return savedRows.some((r) => (r.phone || "").trim() === p);
    };

    return (
        <div
            className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            onClick={() => onClose(false)}
            data-testid="team-managers-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl my-4"
            >
                <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0a0a0a] z-10">
                    <div className="min-w-0">
                        <h2 className="font-display text-xl uppercase tracking-widest truncate">Responsables</h2>
                        <div className="text-xs text-white/50 truncate">{teamName}</div>
                    </div>
                    <button onClick={() => onClose(false)} className="text-white/60 hover:text-white" data-testid="btn-managers-close">
                        <XIcon size={20} />
                    </button>
                </div>

                <div className="p-4 sm:p-5 space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={24} className="animate-spin text-[#4FE0D5]" />
                        </div>
                    ) : (
                        <>
                            <div className="text-xs text-white/60 flex items-start gap-2">
                                <Star size={12} className="text-[#7FD93C] shrink-0 mt-0.5" />
                                <span>Le premier responsable est le <b className="text-white">principal</b> (celui synchronisé avec l&apos;inscription originale). Utilisez « Rendre principal » pour changer l&apos;ordre.</span>
                            </div>

                            {rows.map((row, idx) => (
                                <div
                                    key={idx}
                                    className={`glass-card p-3 sm:p-4 space-y-2 ${idx === 0 ? "border-l-4 border-[#7FD93C]" : "border-l-4 border-white/10"}`}
                                    data-testid={`manager-row-${idx}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 text-xs">
                                            {idx === 0 ? (
                                                <span className="px-2 py-0.5 bg-[#7FD93C]/20 text-[#7FD93C] font-bold uppercase tracking-wider flex items-center gap-1">
                                                    <Star size={10} /> Principal
                                                </span>
                                            ) : (
                                                <span className="text-white/50 uppercase tracking-wider">Responsable #{idx + 1}</span>
                                            )}
                                        </div>
                                        <div className="flex gap-1 flex-wrap justify-end">
                                            {isSavedPhone(row.phone) && (
                                                <button
                                                    type="button"
                                                    onClick={() => sendSms(idx)}
                                                    className="btn-ghost text-xs flex items-center gap-1 border-[#4FE0D5]/40 text-[#4FE0D5]"
                                                    data-testid={`btn-sms-manager-${idx}`}
                                                    title="Envoyer le lien de connexion par SMS"
                                                    disabled={smsBusy === idx}
                                                >
                                                    {smsBusy === idx ? (
                                                        <Loader2 size={12} className="animate-spin" />
                                                    ) : smsResult[idx]?.ok ? (
                                                        <CheckCircle2 size={12} className="text-[#7FD93C]" />
                                                    ) : (
                                                        <Send size={12} />
                                                    )}
                                                    SMS
                                                </button>
                                            )}
                                            {idx !== 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => makePrimary(idx)}
                                                    className="btn-ghost text-xs flex items-center gap-1"
                                                    data-testid={`btn-make-primary-${idx}`}
                                                    title="Rendre principal"
                                                >
                                                    <Star size={12} /> Rendre principal
                                                </button>
                                            )}
                                            {rows.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeRow(idx)}
                                                    className="btn-ghost text-xs text-rose-400 border-rose-400/30"
                                                    data-testid={`btn-remove-manager-${idx}`}
                                                    title="Supprimer"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-2">
                                        <div>
                                            <label className="label-mini">Nom complet *</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                value={row.name}
                                                onChange={(e) => updateRow(idx, "name", e.target.value)}
                                                placeholder="ex. Jean Tremblay"
                                                data-testid={`inp-manager-name-${idx}`}
                                            />
                                        </div>
                                        <div>
                                            <label className="label-mini">Courriel</label>
                                            <input
                                                type="email"
                                                className="input-field"
                                                value={row.email}
                                                onChange={(e) => updateRow(idx, "email", e.target.value)}
                                                placeholder="courriel@exemple.com"
                                                data-testid={`inp-manager-email-${idx}`}
                                            />
                                        </div>
                                        <div>
                                            <label className="label-mini">Téléphone</label>
                                            <input
                                                type="tel"
                                                className="input-field"
                                                value={row.phone}
                                                onChange={(e) => updateRow(idx, "phone", e.target.value)}
                                                placeholder="514 555-1234"
                                                data-testid={`inp-manager-phone-${idx}`}
                                            />
                                        </div>
                                    </div>
                                    {smsResult[idx] && (
                                        <div className={`text-xs mt-1 flex items-center gap-1 ${smsResult[idx].ok ? "text-[#7FD93C]" : "text-rose-400"}`} data-testid={`sms-result-${idx}`}>
                                            {smsResult[idx].ok ? (
                                                <><CheckCircle2 size={12} /> SMS envoyé avec succès</>
                                            ) : (
                                                <><AlertCircle size={12} /> Échec SMS : {smsResult[idx].reason || "inconnu"}</>
                                            )}
                                        </div>
                                    )}
                                    {!isSavedPhone(row.phone) && row.phone && (
                                        <div className="text-[10px] text-white/40 italic">
                                            Enregistrez d&apos;abord pour pouvoir envoyer le SMS de connexion à ce numéro.
                                        </div>
                                    )}
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={addRow}
                                className="btn-ghost w-full justify-center flex items-center gap-2 border-dashed"
                                data-testid="btn-add-manager"
                            >
                                <Plus size={14} /> Ajouter un responsable
                            </button>

                            {error && (
                                <div className="text-rose-400 text-sm flex items-center gap-2" data-testid="managers-error">
                                    <AlertCircle size={14} /> {error}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="p-4 border-t border-white/10 flex justify-end gap-2 items-center sticky bottom-0 bg-[#0a0a0a]">
                    {saveOk && (
                        <span className="text-xs text-[#7FD93C] flex items-center gap-1 mr-auto" data-testid="save-ok">
                            <CheckCircle2 size={14} /> Enregistré. Vous pouvez maintenant envoyer les SMS.
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => onClose(true)}
                        className="btn-ghost"
                        disabled={saving}
                        data-testid="btn-managers-cancel"
                    >
                        Fermer
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        className="btn-primary flex items-center gap-2"
                        disabled={saving || loading}
                        data-testid="btn-managers-save"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}

// Small icon-friendly helper wrapper for use in tables.
export const ManagersButton = ({ onClick, count, testId }) => (
    <button
        onClick={onClick}
        className="btn-ghost p-2 flex items-center gap-1 text-xs"
        title="Gérer les responsables"
        data-testid={testId}
    >
        <User size={12} />
        {typeof count === "number" && count > 0 && <span className="text-[10px]">{count}</span>}
    </button>
);
